'use client';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useLocale } from 'use-intl';
import SectionTitle from './SectionTitle';
import styles from './HighlightsSection.module.css';

interface HighlightsSectionProps {
  data?: any;
  title?: string;
  site?: 'zh' | 'en';
}

/** 按站点切换的 CDN 基础域名 */
const CDN_BASE_MAP: Record<'zh' | 'en', string> = {
  zh: 'https://open-tech-cdn.ssv.qq.com',
  en: 'https://open-tech-cdn.ssv.qq.com',
};

const toPreviewUrl = (path: string, site: 'zh' | 'en' = 'zh'): string => {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  return `${CDN_BASE_MAP[site]}/${path.replace(/^\//, '')}`;
};

const splitTitleChars = (text: string) => {
  const Segmenter = (Intl as unknown as {
    Segmenter?: new (
      locale?: string,
      options?: { granularity: 'grapheme' }
    ) => { segment: (value: string) => Iterable<{ segment: string }> };
  }).Segmenter;

  if (Segmenter) {
    const segmenter = new Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(text), (segment) => segment.segment);
  }
  return Array.from(text);
};

function SingleLineClippedTitle({ title }: { title: string }) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [visibleTitle, setVisibleTitle] = useState(title);

  const syncVisibleTitle = useCallback(() => {
    const element = titleRef.current;
    if (!element) return;

    const availableWidth = element.clientWidth;
    if (availableWidth <= 0) return;

    const computedStyle = window.getComputedStyle(element);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    context.font = computedStyle.font;
    const chars = splitTitleChars(title);
    let start = 0;
    let end = chars.length;
    let matchedLength = chars.length;
    const safeWidth = Math.max(0, availableWidth - 1);

    while (start <= end) {
      const middle = Math.floor((start + end) / 2);
      const nextText = chars.slice(0, middle).join('');
      if (context.measureText(nextText).width <= safeWidth) {
        matchedLength = middle;
        start = middle + 1;
      } else {
        end = middle - 1;
      }
    }

    const nextVisibleTitle = chars.slice(0, matchedLength).join('');
    setVisibleTitle((prev) =>
      prev === nextVisibleTitle ? prev : nextVisibleTitle
    );
  }, [title]);

  useLayoutEffect(() => {
    let disposed = false;
    setVisibleTitle(title);
    syncVisibleTitle();

    const element = titleRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(syncVisibleTitle);
    resizeObserver.observe(element);
    document.fonts?.ready.then(() => {
      if (!disposed) {
        syncVisibleTitle();
      }
    });

    return () => {
      disposed = true;
      resizeObserver.disconnect();
    };
  }, [syncVisibleTitle, title]);

  return (
    <h3 ref={titleRef} className={styles.cardTitle} title={title}>
      {visibleTitle}
    </h3>
  );
}

export default function HighlightsSection({
  data,
  title,
  site = 'zh',
}: HighlightsSectionProps) {
  const locale = useLocale();
  const isZh = locale === 'zh';
  const sectionClassName = `${styles.section} ${
    site === 'en' ? styles.enSection : ''
  }`;

  const highlights = Array.isArray(data)
    ? data.slice(0, 4).map((item: any) => ({
        title: isZh ? (item.title_zh || '--') : (item.title_en || '--'),
        desc: isZh ? (item.desc_zh || '--') : (item.desc_en || '--'),
        icon: item.icon_image_url || item.icon_image || '',
        bgImage: item.bg_image_url || item.bg_image || '',
      }))
    : [];

  if (!highlights.length) {
    return null;
  }

  const cardStyles = [styles.cardFrontier, styles.cardFocus, styles.cardEco];
  const gridCountClassMap: Record<number, string> = {
    1: styles.singleCardGrid,
    2: styles.twoCardGrid,
    3: styles.threeCardGrid,
    4: styles.fourCardGrid,
  };
  const cardGridClassName = `${styles.cardGrid} ${gridCountClassMap[highlights.length] || ''}`;

  const splitDesc = (desc: string) =>
    desc
      .split('/')
      .map((item) => item.trim())
      .filter(Boolean);

  return (
    <section className={sectionClassName}>
      <div className={styles.container}>
        <SectionTitle className="text-[#fff] hidden md:block">{title}</SectionTitle>

        <div className={cardGridClassName}>
          {highlights.map((highlight: any, index: number) => (
            <div key={index} className={styles.cardRow}>
              <div
                className={`${styles.card} ${cardStyles[index % cardStyles.length]}`}
                style={
                  highlight.bgImage
                    ? {
                        backgroundImage: `url(${toPreviewUrl(highlight.bgImage, site)})`,
                        backgroundSize: 'cover',
                      }
                    : undefined
                }
              >
                {/* 右上角装饰图标 */}
                {highlight.icon && (
                  <img
                    className={styles.cardDecor}
                    src={toPreviewUrl(highlight.icon, site)}
                    alt=""
                  />
                )}
                <div className={styles.cardTextLayer}>
                  <SingleLineClippedTitle title={highlight.title} />
                  <hr className={styles.cardDivider} />
                  <div className={styles.cardDesc}>
                    <span className={styles.desktopDesc}>
                      {highlight.desc}
                    </span>
                    <span className={styles.mobileDesc}>
                      {splitDesc(highlight.desc).map((item, itemIndex) => (
                        <span key={itemIndex} className={styles.descItem}>
                          {item}
                        </span>
                      ))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
